// redux/slices/standaloneReceiptsSlice.js — WEB
// ── Свободные чеки (отдельно от встреч и заказов) ──
// Список, сохранение выданного чека и сохранение зикуя.
// Сами документы создаются через createTakbullReceipt / createTakbullCredit
// (takbullCreateSlice). Запросы — через apiRequest (JWT добавляется сам).
//
// ВАЖНО: 403 от /standalone-receipts означает «Takbull не подключён»,
// а не просроченный токен — поэтому токен здесь НЕ сбрасываем
// (в отличие от takbullConnect.handleAuthError).
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { apiRequest } from "../../Utils/apiClient";

/* ===================== HELPERS ===================== */

const getError = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const getUid = (getState, providedUid) => {
  const state = getState();
  const uid =
    providedUid ||
    state?.master?.master?.uid ||
    state?.master?.uid ||
    state?.auth?.uid ||
    null;
  if (!uid) throw new Error("UID required");
  return uid;
};

/* ===================== THUNKS ===================== */

// Список чеков мастера (новые сверху), с пагинацией
export const fetchStandaloneReceipts = createAsyncThunk(
  "standaloneReceipts/fetch",
  async ({ uid: uidArg, offset = 0, limit = 30 } = {}, { getState, rejectWithValue }) => {
    try {
      const uid = getUid(getState, uidArg);
      const res = await apiRequest(
        "GET",
        `/standalone-receipts/${encodeURIComponent(uid)}?offset=${offset}&limit=${limit}`
      );
      return { ...(res?.data || {}), offset };
    } catch (error) {
      console.error("❌ [fetchStandaloneReceipts]", error?.response?.status, error?.response?.data || error?.message);
      return rejectWithValue(getError(error, "Fetch standalone receipts failed"));
    }
  }
);

// Сохранить чек, уже созданный в Takbull
export const saveStandaloneReceipt = createAsyncThunk(
  "standaloneReceipts/save",
  async (receipt = {}, { getState, rejectWithValue }) => {
    try {
      const uid = getUid(getState, receipt.uid);
      const res = await apiRequest("POST", "/standalone-receipts", { ...receipt, uid });
      if (!res?.data?.id) throw new Error("No receipt returned from server.");
      return res.data;
    } catch (error) {
      console.error("❌ [saveStandaloneReceipt]", receipt?.receiptNumber, error?.response?.status, error?.response?.data || error?.message);
      return rejectWithValue(getError(error, "Save standalone receipt failed"));
    }
  }
);

// Сохранить зикуй, уже созданный в Takbull
export const saveStandaloneCredit = createAsyncThunk(
  "standaloneReceipts/saveCredit",
  async ({ id, uid: uidArg, creditNumber, creditUniqId, creditUrl } = {}, { getState, rejectWithValue }) => {
    try {
      if (!id) throw new Error("Receipt id required");
      const uid = getUid(getState, uidArg);
      const res = await apiRequest("PATCH", `/standalone-receipts/${encodeURIComponent(id)}/credit`, {
        uid,
        creditNumber,
        creditUniqId,
        creditUrl,
      });
      if (!res?.data?.id) throw new Error("No receipt returned from server.");
      return res.data;
    } catch (error) {
      console.error("❌ [saveStandaloneCredit]", creditNumber, error?.response?.status, error?.response?.data || error?.message);
      return rejectWithValue(getError(error, "Save standalone credit failed"));
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  items: [],
  total: 0,
  hasMore: false,
  status: "idle",
  error: null,
};

const upsertRow = (state, row) => {
  if (!row || row.id == null) return;
  const index = state.items.findIndex((r) => String(r.id) === String(row.id));
  if (index === -1) {
    state.items.unshift(row);
    state.total += 1;
  } else {
    state.items[index] = row;
  }
};

/* ===================== SLICE ===================== */

const standaloneReceiptsSlice = createSlice({
  name: "standaloneReceipts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStandaloneReceipts.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchStandaloneReceipts.fulfilled, (state, action) => {
        const { rows, total, hasMore, offset } = action.payload || {};
        const incoming = Array.isArray(rows) ? rows : [];

        if (!offset) {
          state.items = incoming;
        } else {
          const known = new Set(state.items.map((r) => String(r.id)));
          incoming.forEach((r) => {
            if (!known.has(String(r.id))) state.items.push(r);
          });
        }

        state.total = Number(total) || state.items.length;
        state.hasMore = Boolean(hasMore);
        state.status = "succeeded";
      })
      .addCase(fetchStandaloneReceipts.rejected, (state, action) => {
        // кэш не трогаем — оставляем последние валидные данные
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(saveStandaloneReceipt.fulfilled, (state, action) => {
        upsertRow(state, action.payload);
      })
      .addCase(saveStandaloneCredit.fulfilled, (state, action) => {
        upsertRow(state, action.payload);
      })

      .addCase(resetStore, () => initialState);
  },
});

export default standaloneReceiptsSlice.reducer;

/* ===================== SELECTORS ===================== */

export const selectStandaloneReceipts = (state) => state?.standaloneReceipts || initialState;